import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, BarChart3 } from "lucide-react";
import AuthDropdown from "../auth/AuthDropdown";
import ThemeToggle from "./ThemeToggle";
import ClearChatConfirmation from "./ClearChatConfirmation";
import { usePopup } from "../context/PopupContext";
import { useAuth } from "../auth/AuthContext";
import { useCart } from "../context/CartContext";

const Navbar = ({ hideNavbar, isMobile, isTablet, isMobileOrTablet, currentOrientation, isFocusMode, messages, clearChatHistory }) => {
  const { user } = useAuth();
  const { cartCount } = useCart();
  const userMembership = user?.['custom:membership'];
  const isAdmin = userMembership === 'admin' || userMembership === 'owner';
  const [isOpen, setIsOpen] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const menuRef = useRef(null);
  const navLinksRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });
  const location = useLocation();
  const { showPopup } = usePopup();

  // Check if we're on Chatty page
  const isChattyPage = location.pathname === '/chatty';

  // Clear chat history confirmation handlers
  const handleClearConfirm = async () => {
    try {
      await clearChatHistory();
      setShowClearConfirm(false);
      setIsOpen(false); // Close menu after clearing
      showPopup('All chat history has been successfully deleted', 'success', { duration: 3000 });
    } catch (error) {
      console.error('Failed to clear chat history:', error);
      showPopup('Failed to clear chat history. Please try again.', 'error', { duration: 3000 });
    }
  };

  const handleClearCancel = () => {
    setShowClearConfirm(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
          setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update sliding indicator position
  useEffect(() => {
    const updateIndicator = () => {
      if (!navLinksRef.current) return;
      const activeLink = navLinksRef.current.querySelector('.nav-link-modern.active');
      if (activeLink) {
        const containerRect = navLinksRef.current.getBoundingClientRect();
        const linkRect = activeLink.getBoundingClientRect();
        setIndicatorStyle({
          left: linkRect.left - containerRect.left,
          width: linkRect.width,
          opacity: 1
        });
      } else {
        setIndicatorStyle(prev => ({ ...prev, opacity: 0 }));
      }
    };

    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [location.pathname]);

  // In Navbar.jsx
// useEffect(() => {
//   console.log('Navbar received hideNavbar:', hideNavbar, 
//     'isMobile:', isMobile, 
//     'orientation:', currentOrientation);
// }, [hideNavbar, isMobile, currentOrientation]);

  // Function to determine if link is active
  const isActive = (path) => {
    // Get current path
    const currentPath = location.pathname;
    
    // Special case for Projects menu item
    if (path === '/projects') {
      return currentPath === '/projects' || 
             currentPath.startsWith('/topics/') ||
             currentPath.includes('kubernet'); // For specific topics like kubernetes
    }
    
    // For Blog, check if on main blog page or specific blog pages
    if (path === '/blog') {
      return currentPath === '/blog' || 
             currentPath.startsWith('/blog/') ||  // Any blog subpages
             currentPath.startsWith('/posts/') || 
             currentPath.startsWith('/articles/');
    }
    
    // For other navigation items, check both exact match and child routes
    // This handles /about/team, /contact/form, etc.
    if (path !== '/') { // Skip this for home page
      if (currentPath === path || currentPath.startsWith(path + '/')) {
        return true;
      }
    }
    
    // Default to exact matching for everything else
    return currentPath === path;
  };

  // Dynamic styles for different screen sizes and orientations
  // Tablet: treat like mobile for hide/show behavior but use desktop height
  const getNavbarStyles = () => {
    // Mobile phone portrait
    if (isMobile && currentOrientation === 'portrait') {
      return {
        top: (hideNavbar || isFocusMode) ? '-55px' : '0',
        height: '48px',
        opacity: (hideNavbar || isFocusMode) ? 0 : 1,
        transition: 'top 200ms ease-in-out, opacity 400ms linear',
        pointerEvents: 'auto'
      };
    }
    // Mobile phone landscape
    if (isMobile && currentOrientation === 'landscape') {
      return {
        top: isFocusMode ? '-40px' : '0',
        height: '36px',
        opacity: isFocusMode ? 0 : 1,
        transition: 'top 200ms ease-in-out, opacity 400ms linear',
        pointerEvents: 'auto'
      };
    }
    // Tablet (any orientation) - hide/show like mobile but use standard height
    if (isTablet) {
      return {
        top: (hideNavbar || isFocusMode) ? '-55px' : '0',
        height: '48px',
        opacity: (hideNavbar || isFocusMode) ? 0 : 1,
        transition: 'top 200ms ease-in-out, opacity 400ms linear',
        pointerEvents: 'auto'
      };
    }
    // Desktop - static navbar (no hide/show unless focus mode)
    return {
      top: isFocusMode ? '-55px' : '0',
      opacity: isFocusMode ? 0 : 1,
      transition: 'top 200ms ease-in-out, opacity 400ms linear',
      pointerEvents: 'auto'
    };
  };
  const navbarStyles = getNavbarStyles();

  return (
    <>
    <nav
      ref={menuRef}
      className={`fixed left-0 w-full flex justify-between lg:justify-center items-center z-[60] ${
        isMobile && currentOrientation === 'landscape'
          ? 'py-1 px-4'  // Smaller padding for mobile landscape
          : isTablet
            ? 'py-2 px-8'  // Slightly more padding for tablets
            : 'py-2 px-6'  // Normal padding for other modes
      }`}
      style={{
        ...navbarStyles
      }}
      onTransitionEnd={() => {
        // Transition ended event handler
        // console.log('Navbar Transition Ended:', {
        //   top: (hideNavbar) ? '-55px' : '0',
        //   opacity: (hideNavbar) ? 0 : 1
        // });
      }}
    >
      {/* No logo on mobile — matches the apex (limkc.com) nav: burger centered,
          theme toggle flush right. Desktop already hid the logo to center the pill. */}

      {/* Desktop Navbar - Modern Pills */}
      <div className="hidden lg:flex items-center gap-2 nav-pill-glass" ref={navLinksRef} style={{ position: 'relative' }}
        onMouseOver={(e) => {
          const link = e.target.closest('.nav-link-modern');
          if (!link || !navLinksRef.current) return;
          const c = navLinksRef.current.getBoundingClientRect();
          const r = link.getBoundingClientRect();
          setIndicatorStyle({ left: r.left - c.left, width: r.width, opacity: 1 });
        }}
        onMouseLeave={() => setIndicatorStyle((p) => ({ ...p, opacity: 0 }))}
      >
        {/* Sliding highlight pill */}
        <div
          className="nav-indicator"
          style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            height: '32px',
            background: 'rgba(255, 255, 255, 0.22)',
            borderRadius: '999px',
            transition: 'left 0.42s cubic-bezier(0.34,1.3,0.4,1), width 0.42s cubic-bezier(0.34,1.3,0.4,1), opacity 0.3s ease',
            filter: 'blur(0.4px)',
            left: indicatorStyle.left,
            width: indicatorStyle.width,
            opacity: indicatorStyle.opacity,
            pointerEvents: 'none',
            zIndex: 0
          }}
        />
        {/* Public pages live on limkc.com (Astro). These are real cross-domain
            links OUT of the Chatty app — not internal SPA routes. */}
        <a
          href="https://limkc.com/"
          className="nav-link-modern text-sm"
        >
          Media
        </a>

        <a
          href="https://limkc.com/projects"
          className="nav-link-modern text-sm"
        >
          Projects
        </a>

        <a
          href="https://limkc.com/about"
          className="nav-link-modern text-sm"
        >
          About
        </a>

        <a
          href="https://limkc.com/gadgets"
          className="nav-link-modern text-sm"
        >
          Gadgets
        </a>

        <a
          href="https://limkc.com/contact"
          className="nav-link-modern text-sm"
        >
          Contact
        </a>

        <a
          href="https://limkc.com/arts"
          className="nav-link-modern text-sm"
        >
          Gallery
        </a>

        <a
          href="https://limkc.com/cart"
          className="nav-link-modern text-sm relative"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          {cartCount > 0 && (
            <span className={`absolute -top-1 -right-1 bg-blue-600 text-xs flex items-center justify-center rounded-full cart-badge ${cartCount > 9 ? 'min-w-5 h-5 px-1' : 'w-4 h-4'}`}>
              {cartCount > 99 ? '99+' : cartCount}
            </span>
          )}
        </a>

        {/* ADMIN/OWNER ONLY: Analytics Link */}
        {isAdmin && (
          <Link
            to="/analytics"
            className={`nav-link-modern text-sm ${isActive('/analytics') ? 'active' : ''}`}
            style={{ color: 'var(--color-accent-orange)', fontWeight: '600' }}
          >
            <BarChart3 size={16} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
            Analytics
          </Link>
        )}

        <div className="ml-4 flex items-center">
          <AuthDropdown closeNavbarMenu={() => setIsOpen(false)}/>
        </div>
      </div>

      {/* Theme toggle isolated at top-right, OUTSIDE the pill (matches the Astro
          nav). Desktop only — mobile keeps its own toggle next to the burger. */}
      <div className="hidden lg:flex items-center absolute right-6 top-1/2 -translate-y-1/2 z-[61]">
        <ThemeToggle />
      </div>

      {/* Mobile burger — centered (matches apex). Absolute so it centers regardless
          of the toggle on the right. */}
      <button
        className="lg:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 p-2 w-10 h-10 flex items-center justify-center"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open menu"
      >
        {/* Icon Wrapper (Ensures Both Icons Stay in Same Position) */}
        <div className="relative w-6 h-6">
          {/* Menu Icon (☰) */}
          <Menu
            className={`absolute inset-0 w-6 h-6 transition-transform duration-300 ${isOpen ? "opacity-0 scale-75" : "opacity-100 scale-100"}`}
            style={{ color: 'var(--text-primary)' }}
          />

          {/* X Icon (✖) */}
          <X
            className={`absolute inset-0 w-6 h-6 transition-transform duration-300 ${isOpen ? "opacity-100 scale-100 rotate-180" : "opacity-0 scale-75"}`}
            style={{ color: 'var(--text-primary)' }}
          />
        </div>
      </button>

      {/* Mobile theme toggle — flush right (matches apex). */}
      <div className="lg:hidden absolute right-4 top-1/2 -translate-y-1/2 flex items-center">
        <ThemeToggle />
      </div>

      {/* Mobile Dropdown Menu (Visible When Open) */}
      {isOpen && (
        <div className="absolute font-bold top-[52px] left-0 w-full flex flex-col items-center py-4 space-y-1 lg:hidden z-[60]"
          style={{
            backgroundColor: 'var(--glass-bg)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: 'var(--shadow-navbar)'
          }}>
          <Link 
            to="/"
            className={`transition ${isActive('/') ? 'text-red-600 font-bold' : 'hover:text-blue-500'}`}
            style={{ color: isActive('/') ? '#DC2626' : 'var(--text-primary)' }}
            onClick={() => setIsOpen(false)}
          >
            Media
          </Link>
          
          {/* Blog hidden - uncomment to restore
          <Link
            to="/blog"
            className={`transition ${isActive('/blog') ? 'text-red-600 font-bold' : 'hover:text-blue-500'}`}
            style={{ color: isActive('/blog') ? '#DC2626' : 'var(--text-primary)' }}
            onClick={() => setIsOpen(false)}
          >
            Blog
          </Link>
          */}
          {/* <Link
            to="/editor"
            className={`transition ${isActive('/editor') ? 'text-red-600 font-bold' : 'hover:text-blue-500'}`}
            style={{ color: isActive('/editor') ? '#DC2626' : 'var(--text-primary)', pointerEvents: 'auto' }}
            onClick={() => setIsOpen(false)}
          >
            Editor
          </Link> */}
          <Link 
            to="/projects"
            className={`transition ${isActive('/projects') ? 'text-red-600 font-bold' : 'hover:text-blue-500'}`}
            style={{ color: isActive('/projects') ? '#DC2626' : 'var(--text-primary)' }}
            onClick={() => setIsOpen(false)}
          >
            Projects
          </Link>
          <Link
            to="/about"
            className={`transition ${isActive('/about') ? 'text-red-600 font-bold' : 'hover:text-blue-500'}`}
            style={{ color: isActive('/about') ? '#DC2626' : 'var(--text-primary)' }}
            onClick={() => setIsOpen(false)}
          >
            About
          </Link>
          <Link
            to="/gadgets"
            className={`transition ${isActive('/gadgets') ? 'text-red-600 font-bold' : 'hover:text-blue-500'}`}
            style={{ color: isActive('/gadgets') ? '#DC2626' : 'var(--text-primary)' }}
            onClick={() => setIsOpen(false)}
          >
            Gadgets
          </Link>
          <Link
            to="/contact"
            className={`transition ${isActive('/contact') ? 'text-red-600 font-bold' : 'hover:text-blue-500'}`}
            style={{ color: isActive('/contact') ? '#DC2626' : 'var(--text-primary)' }}
            onClick={() => setIsOpen(false)}
          >
            Contact
          </Link>
          <Link
            to="/arts"
            className={`transition ${isActive('/arts') ? 'text-red-600 font-bold' : 'hover:text-blue-500'}`}
            style={{ color: isActive('/arts') ? '#DC2626' : 'var(--text-primary)' }}
            onClick={() => setIsOpen(false)}
          >
            Gallery
          </Link>
          <Link
            to="/cart"
            className={`transition flex items-center gap-2 ${isActive('/cart') ? 'text-red-600 font-bold' : 'hover:text-blue-500'}`}
            style={{ color: isActive('/cart') ? '#DC2626' : 'var(--text-primary)' }}
            onClick={() => setIsOpen(false)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Cart {cartCount > 0 && `(${cartCount})`}
          </Link>

          {/* ADMIN/OWNER ONLY: Analytics Link - Mobile */}
          {isAdmin && (
            <Link
              to="/analytics"
              className="mobile-menu-link"
              onClick={() => setIsOpen(false)}
              style={{ color: 'var(--color-accent-orange)', fontWeight: '600' }}
            >
              <BarChart3 size={18} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
              Analytics
            </Link>
          )}

          <AuthDropdown closeNavbarMenu={() => setIsOpen(false)}/>
        </div>
      )}

    </nav>

    {/* Clear Chat Confirmation Popup - Outside nav to avoid z-index stacking context */}
    <ClearChatConfirmation
      isOpen={showClearConfirm}
      onConfirm={handleClearConfirm}
      onCancel={handleClearCancel}
    />
    </>
  );
};

export default Navbar;